import { validationMessages } from "../common/validationMessages";
import * as Yup from "yup";
export interface WeekInfo {
  weekStartDate: string;   // ISO date string (YYYY-MM-DD)
  description: string;
  division: string;
  scheduleType: string;
  status?: string;
  dispatches?: number;
  createdAt?: string;
  lastModifiedAt?: string;
}

export interface WeekSummaryTiles {
  division: string;
  weekStartDate: string;
  totalDispatches: number;
  xgsDispatches: number;
  externalDispatches: number;
  externalCost: number | null;
}

export interface LaneTerminal {
  terminalNumber: number;
  terminalName: string;
  airportCode: string;
  region?: string;
}

export interface Manifest {
  id: number;
  dayOfWeek: number;            // 1 = Monday, etc.
  driverName: string | null;
  status: "PLANNING" | "ASSIGNED" | "COMPLETED" | string;
  carrierNumber: string | null;
  carrierName: string | null;
  originName: string | null;
  destinationName: string | null;
  origin?: LaneTerminal | null;
  stops?: LaneTerminal[];
  destination?: LaneTerminal | null;
  trailerNumber: string | null;
  manifestType: string | null;
  turnDescription: string;
  turnNumber: string;
  routeNumber: string | null;
  teamRequired: "0" | "1";       // looks like backend sends string
  tractorNumber: string | null;
  driverId: number | null;
  division: string;
  weekStartDate: string;         // ISO date string
  dropCount: number;
  earlyDepartureTime?: string;
  carrierRate?: string | number | null;
  carrierRateUom?: string | null;
  miles?: string | number | null;
  carrierCost?: string | number | null;
  drops?: { miles?: string | number | null }[];
}

export interface ManifestsByDay {
  [dayOfWeek: string]: Manifest[];
}

export const weekOptions = [
  { label: "Monday", value: "1" },
  { label: "Tuesday", value: "2" },
  { label: "Wednesday", value: "3" },
  { label: "Thursday", value: "4" },
  { label: "Friday", value: "5" },
  { label: "Saturday", value: "6" },
  { label: "Sunday", value: "7" },
]

export const teamRequiredOptions = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
]

export const turndropType = [
  { label: "Origin", value: "O" },
  { label: "Destination", value: "F" },
  { label: "Intermediate", value: "S" },
]

export interface CopyLaneToDayPayload {
  division: string;
  weekStartDate: string; // YYYY-MM-DD
  sourceDay: string;
  turnNumber: string;
  targetDay: string;
}

export interface CopyLaneToDayResponse {
  success: boolean;
  message?: string;
}


export interface CloneWeekPayload {
  division: string;
  sourceWeekStartDate: string;
  targetWeekStartDate: string;
  targetWeekDescription?: string;
  exclusions: CloneWeekExclusions[];
}

export interface CloneWeekExclusions {
  dayOfWeek: number;
  fromTime?: string;
  toTime?: string;
}
export interface CloneWeekResponse {
  success: boolean;
  message?: string;
}

export interface CreateLanePayload {
  division: string;
  weekStartDate: string;
  turnNumber: string;
  dayOfWeek: number;
  turnDescription?: string;
  manifestType?: string;
  carrierNumber?: string;
  carrierName?: string;
  trailerNumber?: string;
  tractorNumber?: string;
  driverName?: string;
  driverId?: string;
  routeNumber?: string;
  teamRequired?: boolean;
  notes?: string;
}

export interface CreateLaneResponse {
  success: boolean;
  message?: string;
}

export const createLaneValidationSchema = () => {
  return Yup.object({
    turnNumber: Yup.string().required(validationMessages.required),
    dayOfWeek: Yup.string().required(validationMessages.required),
  });
};

export interface CreateDropResponse {
  success: boolean;
  message?: string;
}

export interface CreateDropPayload {
  planId: number;
  terminalNumber: number;
  city: string;
  state: string;
  /** Omit to append at the end of the route. */
  dropNumber?: number;
}

export interface StaticLoadResponse {
  success: boolean;
  message?: string;
}

export interface BuildWeekPayload {
  division: string;
  weekStartDate: string;
}

export interface CreateWeekPayload {
  division: string;
  weekStartDate: string;
  weekDescription: string;
  status: string;
}

export const statusOptions = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Pending', value: 'PENDING' },
]

export interface CloneManifestChangeLogProjection {
  dayOfWeek?: number;
  changeDescription?: string;
  updatedBy?: string | null;
  createdBy?: string | null;
  updatedByName?: string | null;
  createdByName?: string | null;
  modifiedByName?: string | null;
  division?: string;
  weekStartDate?: string;
  modifiedBy?: string | null;
  planId?: number;
  laneDetails?: string;
  changedAt?: string;
  departure?: string;
  laneId?: string;
  userAction?: string;
  carrier?: string | null;
}

export interface ReviewDispatchPayload {
  division: string;
  weekStartDate: string; // YYYY-MM-DD
}

export type DashboardTerminal = LaneTerminal;

export interface DashboardPlan {
  planId: string;
  carrierRate: number;
  departureTime: string;
  carrierCost: number;
  carrierName?: string;
  carrierNumber?: string;
}

export interface DashboardDayEntry {
  day: string;
  noOfDispatches: number;
  planIds?: string[];
  plans?: DashboardPlan[];
}

export type DashboardLaneEntry = {
  origin: DashboardTerminal;
  stops: DashboardTerminal[];
  destination: DashboardTerminal;
} & Partial<Record<"1" | "2" | "3" | "4" | "5" | "6" | "7", DashboardDayEntry>>;

export type DashboardResponse = DashboardLaneEntry[];

export interface AddDispatchPayload {
  division: string;
  weekStartDate: string; // YYYY-MM-DD
  turnNumber: string;
  description: string;
  dayOfWeek: number;
  carrierName: string;
  carrierNumber: string;
  teamRequired: boolean;
  manifestType: string;
  carrierRateUom: string;
  carrierRate: number;
  originTerminal: number;
  destinationTerminal: number;
  notes?: string;
}

export interface AddDispatchResponse {
  success: boolean;
  message?: string;
}

export interface ReviewDispatchWeekSummary {
  division: string;
  sourceWeekStartDate: string;
  targetWeekStartDate: string;
  scheduleType: string;
  description: string;
  status: string;
}

export interface ReviewDispatchLaneCount {
  totalLanes: number;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

export interface ReviewDispatchCarrierSummary {
  carrierCount: number;
  totalDrops: number;
  carriers: Carrier[];
  externalCarriers: number;
  externalCost: number;
  xgsDispatches: number;
}

export interface Carrier {
  carrierName: string;
  carrierNumber: string;
  dropCount: number;
  laneCount: number;
}

export interface ReviewDispatchResponse {
  weekSummary: ReviewDispatchWeekSummary;
  laneCount: ReviewDispatchLaneCount;
  carrierSummary: ReviewDispatchCarrierSummary;
}

export const DAYS_OF_WEEK = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7
} as const;
